<template>
    <div class="timesheet-grid-frame">
        <div v-show="overflow" ref="top" class="timesheet-horizontal-scroll" tabindex="0"
            role="region" aria-label="Горизонтальная прокрутка табеля сверху" @scroll.passive="sync('top')">
            <div :style="{width: `${contentWidth}px`}"></div>
        </div>
        <slot />
    </div>
</template>

<script>
export default {
    name: 'TimesheetGridFrame',
    props: {api: {type: Object, required: true}},
    data: () => ({overflow: true, contentWidth: 0}),
    mounted() {
        this._viewport = this.$el.querySelector('.timesheet-month-scroll');
        this._table = this._viewport.querySelector('table');
        this.api._gridFrame = this;
        this.setSelection(this.api._cellPicker ? this.api._cellPicker._keys : []);
        this._viewport.addEventListener('scroll', this.onScroll, {passive: true});
        this._viewport.addEventListener('mousemove', this.onPointer);
        this._viewport.addEventListener('mouseleave', this.onLeave);
        document.addEventListener('focusin', this.onFocus);
        window.addEventListener('resize', this.schedule);
        if (typeof ResizeObserver !== 'undefined') {
            this._resize = new ResizeObserver(this.schedule);
            [this._viewport, this._table, this.$refs.top].forEach(node => this._resize.observe(node));
        }
        if (typeof MutationObserver !== 'undefined') {
            // Virtual rows and collapsed headers can replace the highlighted nodes.
            this._mutations = new MutationObserver(this.schedule);
            this._mutations.observe(this._table, {childList: true, subtree: true});
        }
        this.onFocus({target: document.activeElement});
        this.schedule();
    },
    beforeDestroy() {
        if (this.api._gridFrame === this) this.api._gridFrame = null;
        if (this._frame) cancelAnimationFrame(this._frame);
        if (this._resize) this._resize.disconnect();
        if (this._mutations) this._mutations.disconnect();
        this._viewport.removeEventListener('scroll', this.onScroll);
        this._viewport.removeEventListener('mousemove', this.onPointer);
        this._viewport.removeEventListener('mouseleave', this.onLeave);
        document.removeEventListener('focusin', this.onFocus);
        window.removeEventListener('resize', this.schedule);
        this.paint([]);
    },
    methods: {
        setSelection(keys) {
            this._selectedRows = new Set();
            this._selectedDates = new Set();
            keys.forEach(key => {
                const separator = key.indexOf(':');
                this._selectedRows.add(key.slice(0, separator));
                this._selectedDates.add(key.slice(separator + 1));
            });
            this.highlight();
        },
        sync(source) {
            const from = source === 'top' ? this.$refs.top : this._viewport;
            const to = source === 'top' ? this._viewport : this.$refs.top;
            if (from && to && Math.abs(to.scrollLeft - from.scrollLeft) >= 1) to.scrollLeft = from.scrollLeft;
        },
        schedule() {
            if (this._frame) return;
            this._frame = requestAnimationFrame(() => { this._frame = null; this.measure(); this.highlight(); });
        },
        measure() {
            this.measureReconciliationHeader();
            const range = Math.max(0, this._viewport.scrollWidth - this._viewport.clientWidth);
            this.overflow = range > 1;
            // Match scroll ranges, including the viewport's vertical scrollbar.
            const width = this.$refs.top.clientWidth + range;
            if (this.contentWidth !== width) this.contentWidth = width;
            this.$nextTick(() => { if (!this._isDestroyed) this.sync('grid'); });
        },
        measureReconciliationHeader() {
            const table = this._table;
            if (!table || !table.tHead) return;
            let height = 0;
            for (const row of Array.from(table.tHead.rows)) {
                if (row.classList.contains('timesheet-reconciliation-row')) {
                    const value = `${height}px`;
                    if (table.style.getPropertyValue('--reconciliation-top') !== value)
                        table.style.setProperty('--reconciliation-top', value);
                    return;
                }
                // Search feedback and long labels can make the date header taller.
                height += row.getBoundingClientRect().height;
            }
        },
        cell(target) {
            const cell = target && target.closest && target.closest('td[data-line-id][data-work-date]');
            return cell && this._viewport.contains(cell) && cell.dataset.workDate
                ? {lineId: cell.dataset.lineId, date: cell.dataset.workDate} : null;
        },
        onScroll() { this.sync('grid'); this.schedule(); },
        onPointer(event) {
            const cell = this.cell(event.target);
            this._point = cell ? {x: event.clientX, y: event.clientY} : null;
            if (cell && this._hover && cell.lineId === this._hover.lineId && cell.date === this._hover.date) return;
            this._hover = cell;
            this.highlight();
        },
        onLeave() { this._hover = null; this._point = null; this.highlight(); },
        onFocus(event) {
            // Keep context while selecting an option in the shared cell picker.
            if (event.target && event.target.closest && event.target.closest('.timesheet-cell-picker')) return;
            this._focus = this.cell(event.target);
            if (this._focus) { this._hover = null; this._point = null; }
            this.highlight();
        },
        highlight() {
            // Selection takes precedence over the last hovered/focused corner.
            // Inspect only rendered headers/rows, not every selected offscreen cell.
            if (this._selectedRows && this._selectedRows.size) {
                const nodes = [];
                this._table.querySelectorAll('tbody [data-review-line-id]').forEach(row => {
                    if (this._selectedRows.has(row.dataset.reviewLineId)) nodes.push(row.querySelector('.timesheet-month-table__person'));
                });
                this._table.querySelectorAll('thead [data-cursor-date]').forEach(header => {
                    if (this._selectedDates.has(header.dataset.cursorDate)) nodes.push(header);
                });
                this.paint(nodes.filter(Boolean));
                return;
            }
            if (this._point) this._hover = this.cell(document.elementFromPoint(this._point.x, this._point.y));
            const cell = this._hover || this._focus;
            if (!cell) { this.paint([]); return; }
            this.paint([
                this._table.querySelector(`[data-review-line-id="${cell.lineId}"] .timesheet-month-table__person`),
                this._table.querySelector(`thead [data-cursor-date="${cell.date}"]`)
            ].filter(Boolean));
        },
        paint(nodes) {
            (this._highlighted || []).forEach(node => { if (!nodes.includes(node)) node.classList.remove('is-cursor-context'); });
            nodes.forEach(node => { if (!node.classList.contains('is-cursor-context')) node.classList.add('is-cursor-context'); });
            this._highlighted = nodes;
        }
    }
};
</script>

<style>
.timesheet-horizontal-scroll {
    position: sticky; z-index: 10; top: var(--shell-header-height, 56px);
    height: 22px; margin-bottom: 5px; overflow-x: auto; overflow-y: hidden;
    border: 2px solid #8eb9df; border-radius: 11px; background: #dbeaf7;
    box-shadow: 0 2px 7px rgba(25, 100, 165, .18);
    scrollbar-color: #1476c4 #dbeaf7; scrollbar-width: auto;
}
.timesheet-horizontal-scroll:hover { border-color: #4b96d3; box-shadow: 0 3px 10px rgba(25, 100, 165, .26); }
.timesheet-horizontal-scroll > div { height: 1px; }
.timesheet-horizontal-scroll::-webkit-scrollbar:horizontal,
.timesheet-grid-frame .timesheet-month-scroll::-webkit-scrollbar:horizontal { height: 18px; }
.timesheet-horizontal-scroll::-webkit-scrollbar-track:horizontal,
.timesheet-grid-frame .timesheet-month-scroll::-webkit-scrollbar-track:horizontal {
    border-radius: 9px; background: #dbeaf7; box-shadow: inset 0 1px 3px rgba(39, 79, 113, .2);
}
.timesheet-horizontal-scroll::-webkit-scrollbar-thumb:horizontal,
.timesheet-grid-frame .timesheet-month-scroll::-webkit-scrollbar-thumb:horizontal {
    min-width: 72px; border: 3px solid #dbeaf7; border-radius: 9px; background-color: #1476c4;
    background-image: repeating-linear-gradient(90deg, rgba(255,255,255,.1) 0, rgba(255,255,255,.1) 6px, rgba(255,255,255,.28) 6px, rgba(255,255,255,.28) 8px);
    box-shadow: inset 0 0 0 1px rgba(10, 75, 128, .35);
}
.timesheet-horizontal-scroll::-webkit-scrollbar-thumb:horizontal:hover,
.timesheet-grid-frame .timesheet-month-scroll::-webkit-scrollbar-thumb:horizontal:hover { border-width: 2px; background-color: #0868b7; }
.timesheet-grid-frame .timesheet-month-scroll { scrollbar-color: #1476c4 #dbeaf7; scrollbar-width: auto; }
.timesheet-grid-frame .timesheet-month-table .is-cursor-context {
    background-image: linear-gradient(rgba(56, 139, 225, .14), rgba(56, 139, 225, .14)) !important;
}
</style>
